import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavItem from "./SidebarNavItem";

import {
  AccountCircle as AccountCircleIcon,
  EventAvailable as EventAvailableIcon,
  ExpandLess,
  ExpandMore,
  HistoryEdu,
  Home as HomeIcon,
  Dashboard as KanbanIcon,
  PhotoCamera as PhotoCameraIcon,
  Schedule as ScheduleIcon,
  SmartToy as SupportAgentIcon,
  ConfirmationNumber as TicketIcon,
  ViewList,
  LibraryBooks as LibraryBooksIcon,
  LiveTv as LiveTvIcon,
} from "@mui/icons-material";
import {
  Coffee,
  GiftIcon,
  HelpCircle,
  MessageSquare,
  Search,
  Settings,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ZIM } from "zego-zim-web";
import { ZEGO_APP_ID } from "../constants/config";
import { useAuth, useCurrentTalentProfile } from "../hooks";
import { useMessageDataStore } from "../services/store/UseMessageData";
import useThemeStore from "../services/store/useThemeStore";
import { playNotificationSound } from "../utils/notificationSound";
import { getMessageToken } from "../utils/zegoTokenManager";
import { alpha } from "@mui/material/styles";
import {
  AIRBANK_DARK_BORDER_SUBTLE,
  AIRBANK_DARK_CANVAS,
  AIRBANK_LIGHT_CANVAS,
} from "../constants/airbankSurfaces";

const drawerWidth = 240;

const bottomNavSection = {
  items: [
    { text: "settings", icon: <Settings size={20} />, path: "/settings" },
    { text: "help", icon: <HelpCircle size={20} />, path: "/help" },
  ],
};

type UserSidebarProps = {
  open: boolean;
  handleClose: () => void;
  coffeeCallChannel?: string;
  coffeeCallNotification?: any;
  toggleDrawer?: any;
  coffeeJoinChannel?: string | null;
};

const UserSidebar = ({
  open,
  coffeeCallChannel,
  coffeeCallNotification,
  toggleDrawer,
  coffeeJoinChannel,
  handleClose,
}: UserSidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [zegoToken, setZegoToken] = useState<string | null>(null);
  const [zim, setZim] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const {
    unreadMessageCounter,
    setUnreadMessageCounter,
    taanaUnreadMessageCounter,
  } = useMessageDataStore();
  const handleBookingToggle = () => setBookingOpen(!bookingOpen);
  const { t } = useTranslation();

  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const isGuestLite = userInfo?.profile_completed === false;
  const currrentPath = location.pathname;
  const messagePage = currrentPath === "/message";

  const { darkMode: isDarkMode } = useThemeStore();

  // TP/TN toggle logic (mirror BusinessSidebar)
  const isTPPage =
    currrentPath.includes("/talents") || currrentPath === "/talents";
  const isTNPage =
    currrentPath.includes("/TNpage") || currrentPath === "/TNpage";

  const getSavedTPTNPreference = (): "TP" | "TN" => {
    const saved = localStorage.getItem("TPTN_PREFERENCE");
    return saved === "TP" ? "TP" : "TN";
  };

  useEffect(() => {
    if (isTPPage) {
      localStorage.setItem("TPTN_PREFERENCE", "TP");
    } else if (isTNPage) {
      localStorage.setItem("TPTN_PREFERENCE", "TN");
    }
  }, [isTPPage, isTNPage]);

  const preferredView = isTPPage
    ? "TP"
    : isTNPage
      ? "TN"
      : getSavedTPTNPreference();
  const isTPPreferred = preferredView === "TP";

  const handleTPTNToggle = () => {
    if (isTPPage) {
      navigate("/TNpage");
    } else if (isTNPage) {
      navigate("/talents");
    } else {
      if (preferredView === "TP") {
        navigate("/talents");
      } else {
        navigate("/TNpage");
      }
    }
  };

  const topNavSections = [
    {
      items: [
        // TP/TN toggle rendered separately
        // {
        //   text: "Cards Library",
        //   icon: <LibraryBooksIcon />,
        //   path: "/taascards",
        // },
        // { text: "gift_cards", icon: <GiftIcon />, path: "/gift-cards" },
        // { text: "snap_flyers", icon: <HistoryEdu />, path: "/snap-flyers" },
        { text: "search", icon: <Search size={20} />, path: "/search" },

        { text: "Divider", icon: <Divider />, path: "" },

        { text: "my_schedules", icon: <ScheduleIcon />, path: "/schedule" },

        // { text: "stage_cards", icon: <StageIcon />, path: "/stage-cards" },
        { text: "ai_twins", icon: <Users size={20} />, path: "/ai-twins" },
        {
          text: "AiTs",
          icon: <ViewList />,
          path: "/ai-twins/list",
        },

        {
          text: "ait_product_cards",
          icon: <ViewList />,
          path: "/user/ait-product-cards",
        },

        { text: "tana", icon: <SupportAgentIcon />, path: "/taana" },
      ],
    },
    {
      items: [
        { text: "dashboard", icon: <KanbanIcon />, path: "/dashboard" },

        {
          text: "message",
          icon: <MessageSquare size={20} />,
          path: "/message",
        },
        {
          text: "avatar_presentation",
          icon: <LiveTvIcon />,
          path: "/tavus-avatar-presentation/25"
        },
        ...(!isGuestLite
          ? [{ text: "wallet", icon: <Wallet size={20} />, path: "/wallet" }]
          : []),
      ],
    },
  ];

  const userId = `user-${userInfo?.id}`;
  const userName = `${userInfo?.first_name} ${userInfo?.last_name}`;

  useEffect(() => {
    if (zegoToken || !userId) return;

    getMessageToken(userId)
      .then((result) => {
        setZegoToken(result.token);
      })
      .catch((err) => {
        console.error("Failed to get message token:", err.message || err);
      });
  }, [userInfo?.id, currentProfile?.id, userId, zegoToken]);

  const queryConversation = async (zimInstance: any) => {
    try {
      setIsLoading(true);
      const conversationResult = await zimInstance?.queryConversationList({
        count: 20,
      });
      console.log("Conversation query result:", conversationResult);

      // Sort conversations by last message timestamp (most recent first)
      const sortedConversations = (
        conversationResult?.conversationList || []
      ).sort((a: any, b: any) => {
        const timeA = a.lastMessage?.timestamp || 0;
        const timeB = b.lastMessage?.timestamp || 0;
        return timeB - timeA;
      });

      setConversations(sortedConversations);
      const unreadMessageCount = (
        conversationResult?.conversationList || []
      ).reduce(
        (acc: number, conversation: any) =>
          acc + (conversation.unreadMessageCount || 0),
        0
      );
      setUnreadMessageCounter(unreadMessageCount);

      // Auto-select the first conversation if none is selected and conversations exist
      // if (sortedConversations.length > 0 && !selectedUser && !isMobile) {
      //   setSelectedUser(sortedConversations[0]);
      //   // Mark the auto-selected conversation as read
      //   markConversationAsRead(sortedConversations[0]);
      // }
    } catch (error) {
      console.error("Failed to query conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const markConversationAsRead = async (conversation: any) => {
    if (!zim || !conversation) return;

    try {
      await zim.clearConversationUnreadMessageCount(
        conversation.conversationID,
        conversation.conversationType || 0 // 0 for peer-to-peer
      );
      console.log("Conversation marked as read:", conversation.conversationID);
      // Refresh conversation list to update unread count
      queryConversation(zim);
    } catch (error) {
      console.error("Failed to mark conversation as read:", error);
    }
  };

  useEffect(() => {
    if (zegoToken && !messagePage) {
      const initializeZIM = async () => {
        try {
          let zimInstance = ZIM?.getInstance();
          if (zimInstance) {
            zimInstance?.logout();
          }

          ZIM.create({ appID: Number(ZEGO_APP_ID) });
          zimInstance = ZIM.getInstance();
          // Setup event listeners
          const handleConnectionStateChanged = (zim: any, info: any) => {
            if (info.state !== 0 || info.state !== 3) {
              // 1 means connected/logged in
              setIsLoggedIn(true);
              queryConversation(zimInstance);
            } else {
              setIsLoggedIn(false);
            }
          };

          const handleError = (info: any) => {
            console.error("ZIM Error:", info);
          };

          const handlePeerMessageReceived = (zim: any, info: any) => {
            if (!info?.info?.isOfflineMessage) {
              playNotificationSound();
              enqueueSnackbar(
                `${info?.messageList?.[0]?.extendedData} : ${info?.messageList?.[0]?.message}`,
                {
                  variant: "info",
                  autoHideDuration: 3000,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                  },
                }
              );
            }
            // Refresh conversation list when new messages arrive
            queryConversation(zimInstance);
          };

          const handleConversationChanged = (zim: any, info: any) => {
            console.log("Conversation changed, updating list:", info);
            queryConversation(zimInstance);
          };

          zimInstance?.on(
            "connectionStateChanged",
            handleConnectionStateChanged
          );
          zimInstance?.on("error", handleError);
          zimInstance?.on("peerMessageReceived", handlePeerMessageReceived);
          zimInstance?.on("conversationChanged", handleConversationChanged);

          // Login to ZIM
          await zimInstance?.login(userId, {
            userName: userName,
            token: zegoToken,
            customStatus: "",
            isOfflineLogin: false,
          });
          zimInstance?.updateUserAvatarUrl(userInfo?.profile_image || "");

          setZim(zimInstance);
          setIsLoggedIn(true);
          queryConversation(zimInstance);
        } catch (error) {
          console.error("Failed to initialize ZIM:", error);
        }
      };

      initializeZIM();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zegoToken, userId, messagePage]); // zim intentionally excluded to avoid recreation loop

  // Cleanup effect for event listeners
  useEffect(() => {
    return () => {
      if (zim) {
        try {
          zim.off("connectionStateChanged");
          zim.off("error");
          zim.off("peerMessageReceived");
          zim.off("conversationChanged");
          zim.logout();
        } catch (error) {
          console.warn("Failed to remove event listeners:", error);
        }
      }
    };
  }, [zim]);

  // Keep only AI Twin navigation for Airbank scope.
  // Other legacy sidebar sections are intentionally not rendered.
  const AIT_ONLY_NAV = true;
  if (AIT_ONLY_NAV) {
    return (
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={handleClose}
        sx={{
          width: open ? 252 : 76,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? 252 : 76,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            background: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
            borderRight: isDarkMode
              ? `1px solid ${AIRBANK_DARK_BORDER_SUBTLE}`
              : "1px solid rgba(226, 232, 240, 0.95)",
            overflowX: "hidden",
            boxSizing: "border-box",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ pt: 1.25 }}>
          <Toolbar
            sx={{
              minHeight: 72,
              px: open ? 2 : 0,
              justifyContent: open ? "flex-start" : "center",
              borderBottom: `1px solid ${isDarkMode ? alpha("#94a3b8", 0.14) : alpha("#0f172a", 0.08)}`,
              background: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Lexend', sans-serif",
                fontWeight: 800,
                letterSpacing: "0.06em",
                fontSize: open ? "1rem" : "0.85rem",
                background: "linear-gradient(90deg, #f59e0b 0%, #ea580c 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {open ? "AIRBANK" : "AB"}
            </Typography>
          </Toolbar>
          <List
            disablePadding
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              px: 0.75,
              pt: 1.5,
            }}
          >
            {open && (
              <Typography
                sx={{
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: isDarkMode ? "rgba(251, 191, 36, 0.9)" : "#b45309",
                }}
              >
                AI Workspace
              </Typography>
            )}
            {/* <NavItem
              item={{ text: "AI Twin", icon: <Users size={20} />, path: "/ai-twins" }}
              open={open}
              selected={location.pathname.startsWith("/ai-twins")}
              onClick={isMobile ? handleClose : undefined}
            /> */}
            <NavItem
              item={{ text: "AI Twins List", icon: <ViewList />, path: "/ai-twins/list" }}
              open={open}
              selected={location.pathname === "/ai-twins/list"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "AI Product Cards",
                icon: <LibraryBooksIcon />,
                path: "/user/ait-product-cards",
              }}
              open={open}
              selected={location.pathname === "/user/ait-product-cards"}
              onClick={isMobile ? handleClose : undefined}
            />
          </List>
          <Divider
            sx={{
              mt: 1.5,
              mx: open ? 1.5 : 1,
              borderColor: isDarkMode
                ? "rgba(148, 163, 184, 0.18)"
                : "rgba(15, 23, 42, 0.08)",
            }}
          />
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={handleClose}
      sx={{
        width: open ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : 72,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: isDarkMode ? AIRBANK_DARK_CANVAS : "var(--neutralWhite)",
          borderRight: isDarkMode
            ? `1px solid ${AIRBANK_DARK_BORDER_SUBTLE}`
            : "1px solid rgba(226, 232, 240, 0.8)",
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: isDarkMode
            ? "0 8px 32px 0 rgba(0, 0, 0, 0.4), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)"
            : "0 8px 32px 0 rgba(0, 0, 0, 0.08), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)",

          scrollbarWidth: "thin",
          scrollbarColor: isDarkMode
            ? "var(--primary-300) transparent"
            : "var(--neutral-400) transparent",

          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDarkMode
              ? "rgba(148, 163, 184, 0.3)"
              : "rgba(148, 163, 184, 0.4)",
            borderRadius: "10px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
            transition: "background-color 0.2s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDarkMode
              ? "rgba(148, 163, 184, 0.5)"
              : "rgba(148, 163, 184, 0.6)",
          },
        },
      }}
    >
      <Box>
        <Toolbar
          sx={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "flex-start" : "center",
            px: open ? 2.5 : 0,
            background: isDarkMode
              ? "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(5, 91, 181, 0.05) 100%)"
              : "linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(5, 91, 181, 0.02) 100%)",
            borderBottom: isDarkMode
              ? "1px solid rgba(148, 163, 184, 0.1)"
              : "1px solid rgba(226, 232, 240, 0.8)",
          }}
        >
          {open && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #197FD3 0%, #005BB5 50%, #197FD3 100%)",
                  backgroundSize: "200% 200%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDarkMode
                    ? "0 4px 12px rgba(25, 118, 210, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "0 4px 12px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                  animation: "shimmer 3s ease-in-out infinite",
                  "@keyframes shimmer": {
                    "0%, 100%": {
                      backgroundPosition: "0% 50%",
                    },
                    "50%": {
                      backgroundPosition: "100% 50%",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    fontFamily: "'Lexend', sans-serif",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  T
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontFamily: "'Lexend', sans-serif",
                  background: isDarkMode
                    ? "linear-gradient(135deg, #E4E4E7 0%, #A1A1AA 100%)"
                    : "linear-gradient(135deg, #1E293B 0%, #475569 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: "1.25rem",
                }}
              >
                TaasNet
              </Typography>
            </Box>
          )}
          {!open && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #197FD3 0%, #005BB5 50%, #197FD3 100%)",
                backgroundSize: "200% 200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isDarkMode
                  ? "0 4px 12px rgba(25, 118, 210, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                  : "0 4px 12px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                animation: "shimmer 3s ease-in-out infinite",
                "@keyframes shimmer": {
                  "0%, 100%": {
                    backgroundPosition: "0% 50%",
                  },
                  "50%": {
                    backgroundPosition: "100% 50%",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "1.1rem",
                  fontFamily: "'Lexend', sans-serif",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                T
              </Typography>
            </Box>
          )}
        </Toolbar>

        <Box sx={{ display: "flex", flexDirection: "column", pt: 1 }}>
          {/* TP/TN Toggle Component */}
          <Box
            sx={{
              mx: 1,
              mb: 0.5,
            }}
          >
            <ListItemButton
              onClick={handleTPTNToggle}
              sx={{
                borderRadius: 2.5,
                minHeight: 52,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 2.5 : 1.5,
                py: 1.4,
                fontFamily: "'Lexend', sans-serif",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "rgba(25, 118, 210, 0.15)"
                      : "rgba(25, 118, 210, 0.1)"
                    : "transparent",
                color:
                  isTPPage || isTNPage
                    ? "var(--primary-700)"
                    : isDarkMode
                      ? "var(--neutral-100)"
                      : "var(--neutral-1000)",
                border:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "1px solid rgba(25, 118, 210, 0.3)"
                      : "1px solid rgba(25, 118, 210, 0.2)"
                    : "1px solid transparent",
                boxShadow:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "0 2px 8px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                      : "0 2px 8px rgba(25, 118, 210, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)"
                    : "none",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.1)"
                    : "rgba(241, 245, 249, 0.8)",
                  color: isDarkMode
                    ? "var(--primary-400)"
                    : "var(--primary-700)",
                  transform: "translateY(-2px)",
                  boxShadow: isDarkMode
                    ? "0 6px 20px rgba(25, 118, 210, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                    : "0 6px 20px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                  border: isDarkMode
                    ? "1px solid rgba(25, 118, 210, 0.4)"
                    : "1px solid rgba(25, 118, 210, 0.3)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 0,
                  justifyContent: "center",
                  color:
                    isTPPage || isTNPage
                      ? "var(--primary-700)"
                      : isDarkMode
                        ? "var(--neutralWhite)"
                        : "#000000",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <HomeIcon sx={{ fontSize: "1.3rem" }} />
              </ListItemIcon>
              {open ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: !isTPPreferred ? 600 : 400,
                      fontSize: "0.875rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: !isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                      transition: "all 0.2s ease",
                    }}
                  >
                    TN
                  </Typography>
                  <Switch
                    checked={isTPPreferred}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTPTNToggle();
                    }}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        color: isDarkMode ? "var(--neutral-400)" : "#000000",
                        "&.Mui-checked": {
                          color: "var(--primary-700)",
                        },
                        "&.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "var(--primary-700)",
                        },
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: isDarkMode
                          ? "var(--neutral-600)"
                          : "var(--neutral-300)",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: isTPPreferred ? 600 : 400,
                      fontSize: "0.875rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                      transition: "all 0.2s ease",
                    }}
                  >
                    TP
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily: "'Lexend', sans-serif",
                      fontWeight: 600,
                      color: isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                    }}
                  >
                    {isTPPreferred ? "TP" : "TN"}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </Box>
          {coffeeJoinChannel && (
            <Box
              sx={{
                background: isDarkMode
                  ? "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)"
                  : "linear-gradient(135deg, rgba(254, 243, 199, 0.9) 0%, rgba(252, 211, 77, 0.6) 100%)",
                borderRadius: 2.5,
                p: 1.5,
                mx: 1,
                border: isDarkMode
                  ? "1px solid rgba(245, 158, 11, 0.3)"
                  : "1px solid rgba(245, 158, 11, 0.4)",
                boxShadow: isDarkMode
                  ? "0 4px 12px rgba(245, 158, 11, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                  : "0 4px 12px rgba(245, 158, 11, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
              }}
            >
              <List
                disablePadding
                sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
              >
                <NavItem
                  item={{
                    text: "Join coffee call",
                    icon: <Coffee size={20} />,
                    path: `/coffee-call/${coffeeJoinChannel}`,
                    fontSize: "0.95rem",
                  }}
                  open={open}
                  selected={true}
                  onClick={isMobile ? handleClose : undefined}
                />
              </List>
            </Box>
          )}

          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {topNavSections[0].items.map((item, index) => {
              if (item.text === "Divider") {
                return (
                  <Divider
                    key={`divider-${index}`}
                    sx={{
                      my: 1.5,
                      mx: 1,
                      borderColor: isDarkMode
                        ? "rgba(148, 163, 184, 0.15)"
                        : "rgba(226, 232, 240, 0.8)",
                      "&::before, &::after": {
                        borderColor: isDarkMode
                          ? "rgba(148, 163, 184, 0.15)"
                          : "rgba(226, 232, 240, 0.8)",
                      },
                    }}
                  />
                );
              }
              return (
                <NavItem
                  key={item.text}
                  item={item}
                  open={open}
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleClose : undefined}
                  badgeContent={
                    taanaUnreadMessageCounter > 0 && item.text === "Tana"
                      ? taanaUnreadMessageCounter
                      : undefined
                  }
                />
              );
            })}
          </List>

          <ListItemButton
            onClick={handleBookingToggle}
            sx={{
              borderRadius: 2.5,
              minHeight: 52,
              mx: 1,
              justifyContent: open ? "flex-start" : "center",
              px: open ? 2.5 : 1.5,
              py: 1.4,
              fontFamily: "'Lexend', sans-serif",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: "transparent",
              color: isDarkMode ? "var(--neutralWhite)" : "#000000",
              border: "1px solid transparent",
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.1)"
                  : "rgba(241, 245, 249, 0.8)",
                color: isDarkMode ? "var(--primary-400)" : "var(--primary-700)",
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 6px 20px rgba(25, 118, 210, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                  : "0 6px 20px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                border: isDarkMode
                  ? "1px solid rgba(25, 118, 210, 0.3)"
                  : "1px solid rgba(25, 118, 210, 0.2)",
                "& .MuiListItemIcon-root": {
                  color: isDarkMode
                    ? "var(--primary-400)"
                    : "var(--primary-700)",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.5 : 0,
                justifyContent: "center",
                color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                transition: "all 0.3s ease",
                fontSize: "1.3rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <EventAvailableIcon />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary={t("sessions")}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    fontFamily: "'Lexend', sans-serif",
                    color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                  },
                }}
              />
            )}
            {open && (bookingOpen ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>

          <Collapse in={bookingOpen} timeout="auto" unmountOnExit>
            <List disablePadding sx={{ pl: open ? 4 : 1, gap: 0.5 }}>
              {[
                { text: "suits", path: "/sessions" },
                { text: "stage", path: "/sessions/stagecard" },
                { text: "tickets", path: "/tickets" },
              ].map((item) => (
                <NavItem
                  key={item.path}
                  item={{
                    text: item.text,
                    icon:
                      item.text === "tickets" ? (
                        <TicketIcon />
                      ) : (
                        <EventAvailableIcon />
                      ),
                    path: item.path,
                  }}
                  open={open}
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleClose : undefined}
                />
              ))}
            </List>
          </Collapse>

          <Divider
            sx={{
              width: open ? "85%" : "60%",
              mx: "auto",
              my: 3,
              borderColor: isDarkMode
                ? "rgba(148, 163, 184, 0.15)"
                : "rgba(226, 232, 240, 0.8)",
              borderWidth: "1px",
              "&::before, &::after": {
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
              },
            }}
          />

          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {topNavSections[1].items.map((item, index) => {
              // Divider case
              if (item.text === "Divider") {
                return (
                  <Divider
                    key={`divider-${index}`}
                    sx={{
                      my: 1.5,
                      mx: 1,
                      borderColor: isDarkMode
                        ? "rgba(148, 163, 184, 0.15)"
                        : "rgba(226, 232, 240, 0.8)",
                      "&::before, &::after": {
                        borderColor: isDarkMode
                          ? "rgba(148, 163, 184, 0.15)"
                          : "rgba(226, 232, 240, 0.8)",
                      },
                    }}
                  />
                );
              }

              // Normal navigation item
              return (
                <NavItem
                  key={item.text}
                  item={item}
                  open={open}
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleClose : undefined}
                  badgeContent={
                    unreadMessageCounter > 0 && item.text === "Message"
                      ? unreadMessageCounter
                      : undefined
                  }
                />
              );
            })}
          </List>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {bottomNavSection.items.map((item) => (
            <NavItem
              key={item.text}
              item={item}
              open={open}
              selected={location.pathname === item.path}
              onClick={isMobile ? handleClose : undefined}
            />
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default UserSidebar;
